"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get DependencySlotMapper () {
        return DependencySlotMapper;
    },
    get DependencySlotTypes () {
        return DependencySlotTypes;
    },
    get dependencySlotMapper () {
        return dependencySlotMapper;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _catchError = require("~/helpers/catchError");
var DependencySlotTypes = /*#__PURE__*/ function(DependencySlotTypes) {
    DependencySlotTypes["STRING"] = "string";
    DependencySlotTypes["NUMBER"] = "number";
    DependencySlotTypes["BOOLEAN"] = "boolean";
    DependencySlotTypes["ARRAY"] = "array";
    DependencySlotTypes["OBJECT"] = "object";
    DependencySlotTypes["TIMESTAMP"] = "timestamp";
    return DependencySlotTypes;
}({});
var DependencyFields = /*#__PURE__*/ function(DependencyFields) {
    // Queryable fields (indexed)
    DependencyFields["QUERYABLE_FIELD_0"] = "queryable_field_0";
    DependencyFields["QUERYABLE_FIELD_1"] = "queryable_field_1";
    DependencyFields["QUERYABLE_FIELD_2"] = "queryable_field_2";
    // Meta field (JSON, not indexed)
    DependencyFields["META"] = "meta";
    return DependencyFields;
}(DependencyFields || {});
let DependencySlotMapper = class DependencySlotMapper {
    /**
   * Check if a field ID represents a queryable (indexed) field
   */ isQueryableField(fieldId) {
        return fieldId === "queryable_field_0" || fieldId === "queryable_field_1" || fieldId === "queryable_field_2";
    }
    /**
   * Get field mapping for a specific dependent type
   */ getMapping(dependentType) {
        return this.mappings[dependentType] || {};
    }
    /**
   * Extract physical database fields from logical dependency info
   * Separates queryable fields from meta fields
   */ extractSlotFields(dependentType, item) {
        const mapping = this.getMapping(dependentType);
        const fields = {};
        const metaFields = {};
        for (const [logicalField, fieldDef] of Object.entries(mapping)){
            let value = item[logicalField];
            // Check required fields
            if (fieldDef.required && value === undefined) {
                _catchError.NcError.badRequest(`Missing required field: ${logicalField}`);
            }
            // Skip undefined optional fields
            if (value === undefined) {
                continue;
            }
            // Validate and coerce types
            value = this.validateAndCoerceValue(value, fieldDef.type, logicalField);
            // Separate queryable fields from meta fields
            if (this.isQueryableField(fieldDef.id)) {
                // Store directly in database column
                fields[fieldDef.id] = value;
            } else {
                // Store in meta JSON
                metaFields[logicalField] = value;
            }
        }
        // If there are meta fields, stringify and store in meta column
        if (Object.keys(metaFields).length > 0) {
            fields["meta"] = JSON.stringify(metaFields);
        }
        return fields;
    }
    /**
   * Hydrate logical fields from physical database data
   * Parses meta JSON and combines with queryable fields
   */ hydrateSlotFields(dependentType, record) {
        const mapping = this.getMapping(dependentType);
        const fields = {};
        // First, extract queryable fields
        for (const [logicalField, fieldDef] of Object.entries(mapping)){
            if (this.isQueryableField(fieldDef.id) && record[fieldDef.id] !== undefined) {
                fields[logicalField] = record[fieldDef.id];
            }
        }
        // Then, parse meta JSON and extract non-queryable fields
        if (record["meta"]) {
            try {
                const metaData = typeof record["meta"] === 'string' ? JSON.parse(record["meta"]) : record["meta"];
                // Only include fields that are defined in the mapping
                for (const [logicalField, fieldDef] of Object.entries(mapping)){
                    if (!this.isQueryableField(fieldDef.id) && metaData[logicalField] !== undefined && metaData[logicalField] !== null) {
                        fields[logicalField] = metaData[logicalField];
                    }
                }
            } catch (e) {
                // If JSON parsing fails, log error but don't break
                console.error('Failed to parse dependency meta field:', e);
            }
        }
        return fields;
    }
    /**
   * Get the physical field ID for a logical field
   * Used for building query conditions
   * Only returns IDs for queryable fields
   */ getSlotId(dependentType, logicalField) {
        const mapping = this.getMapping(dependentType);
        const fieldDef = mapping[logicalField];
        // Only return field ID for queryable fields
        // Meta fields cannot be queried directly
        if (fieldDef && this.isQueryableField(fieldDef.id)) {
            return fieldDef.id;
        }
        return null;
    }
    /**
   * Validate and coerce value to the expected type
   */ validateAndCoerceValue(value, type, fieldName) {
        switch(type){
            case "number":
                if (isNaN(Number(value))) {
                    _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                }
                return Number(value);
            case "boolean":
                return !!value;
            case "array":
                if (typeof value === 'string') {
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                    }
                }
                if (!Array.isArray(value)) {
                    _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                }
                // Keep as array for meta JSON (will be stringified with entire meta object)
                return value;
            case "object":
                if (typeof value === 'string') {
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                    }
                }
                if (typeof value !== 'object' || value === null) {
                    _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                }
                // Keep as object for meta JSON (will be stringified with entire meta object)
                return value;
            case "string":
                if (typeof value !== 'string') {
                    _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                }
                return value;
            case "timestamp":
                // Accept Date objects or ISO strings
                if (value instanceof Date) {
                    return value;
                }
                if (typeof value === 'string') {
                    const date = new Date(value);
                    if (isNaN(date.getTime())) {
                        _catchError.NcError.badRequest(`Invalid timestamp for field: ${fieldName}`);
                    }
                    return date;
                }
                _catchError.NcError.badRequest(`Invalid type for field: ${fieldName}`);
                return value;
            default:
                return value;
        }
    }
    constructor(){
        this.mappings = {
            [_nocodbsdk.DependencyTableType.Widget]: {
                path: {
                    id: "meta",
                    type: "string",
                    required: false
                }
            },
            [_nocodbsdk.DependencyTableType.Workflow]: {
                nodeType: {
                    id: "queryable_field_0",
                    type: "string",
                    required: false
                },
                triggerId: {
                    id: "queryable_field_1",
                    type: "string",
                    required: false
                },
                nextSyncAt: {
                    id: "queryable_field_2",
                    type: "timestamp",
                    required: false
                },
                path: {
                    id: "meta",
                    type: "string",
                    required: false
                },
                nodeId: {
                    id: "meta",
                    type: "string",
                    required: false
                },
                // External trigger state (returned from onActivateHook, needed for cleanup)
                activationState: {
                    id: "meta",
                    type: "object",
                    required: false
                }
            },
            [_nocodbsdk.DependencyTableType.Column]: {},
            [_nocodbsdk.DependencyTableType.Model]: {},
            [_nocodbsdk.DependencyTableType.View]: {}
        };
    }
};
const dependencySlotMapper = new DependencySlotMapper();
